import React, { useState, useRef } from 'react';

interface LogoUploaderProps {
  value?: string;
  onChange: (url: string) => void;
  onRemove: () => void;
  onUploadFile: (file: File) => void;
  defaultLogo?: string;
  maxSize?: number; // MB
  maxHeight?: number;
  maxWidth?: number;
  acceptedTypes?: string[];
}

const LogoUploader: React.FC<LogoUploaderProps> = ({
  value,
  onChange,
  onRemove,
  onUploadFile,
  defaultLogo,
  maxSize = 2,
  maxHeight = 1000,
  maxWidth = 1000,
  acceptedTypes = ['image/jpeg', 'image/png']
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isDefaultLogo = !value && !previewUrl;
  const displayedImage = previewUrl || value || defaultLogo;

  const allowedTypesText = `${acceptedTypes.map(type => 
    type.replace('image/', '').toUpperCase()
  ).join(', ')} up to ${maxSize}MB`;

  const validateDimensions = (file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(img.src);
        
        const errors: string[] = [];
        
        if (img.width > maxWidth) {
          errors.push(`Image width must be no more than ${maxWidth}px`);
        }
        if (img.height > maxHeight) {
          errors.push(`Image height must be no more than ${maxHeight}px`);
        }
        
        if (errors.length > 0) {
          reject(errors.join('. '));
        } else {
          resolve();
        }
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        reject('Failed to load image for dimension validation');
      };
    });
  };

  const validateFile = async (file: File): Promise<string | null> => {
    if (!file) return 'No file selected';
    
    if (!acceptedTypes.includes(file.type)) {
      return `Invalid file type. Please upload ${allowedTypesText}`;
    }
    
    if (file.size > maxSize * 1024 * 1024) {
      return `File size must be less than ${maxSize}MB`;
    }
    
    try {
      await validateDimensions(file);
      return null;
    } catch (error) {
      return error as string;
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      await processFile(file);
    }
    // Reset input value to allow uploading the same file again
    if (event.target) {
      event.target.value = '';
    }
  };

  const processFile = async (file: File) => {
    const validationError = await validateFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setLoading(true);

    try {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      onUploadFile(file);
    } catch (err) {
      setError('Failed to upload image. Please try again.');
      setPreviewUrl('');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    setPreviewUrl('');
    onChange('');
    onRemove();
  };

  return (
    <div className="w-full">
      <div className="relative">
        {/* Upload Container */}
        <div 
          className={`relative border-2 border-dashed rounded-lg p-2 hover:border-gray-400 transition-colors ${
            error ? 'border-red-500' : 'border-gray-300'
          } ${(!value && !previewUrl) ? 'cursor-pointer' : ''}`}
        >
          {/* Current Logo Display */}
          <div className="relative w-full">
            {/* Logo Image */}
            <div className="flex items-center justify-center bg-gray-50 rounded-lg p-2">
              <img
                src={displayedImage}
                className="max-h-16 object-contain"
                alt={isDefaultLogo ? 'Default logo' : 'Uploaded logo'}
              />
            </div>
            
            {/* Status Badge */}
            <div 
              className={`absolute top-2 left-2 px-1.5 py-0.5 rounded text-xs border border-gray-300 ${
                isDefaultLogo ? 'bg-gray-100 text-gray-600' : 'bg-accent-foreground text-accent'
              }`}
            >
              {isDefaultLogo ? 'Default Logo' : 'Custom Logo'}
            </div>
            
            {/* Action Buttons */}
            <div className="absolute top-2 right-2 flex gap-2">
              {/* Upload New Button */}
              <label
                className="cursor-pointer bg-blue-500 text-white rounded-full p-1.5 hover:bg-blue-600 transition-colors"
                title={isDefaultLogo ? 'Upload custom logo' : 'Change logo'}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="sr-only"
                  accept={acceptedTypes.join(',')}
                  onChange={handleFileChange}
                />
              </label>
              
              {/* Delete Button - Only show if using custom logo */}
              {!isDefaultLogo && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition-colors"
                  title="Remove custom logo"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          </div>
          
          {/* Upload Instructions */}
          <div className="mt-4 text-center text-sm text-gray-500">
            <p>
              {isDefaultLogo ? 'Click the upload icon to upload custom logo' : 'Click the upload icon to change or the delete icon to revert to default'}
            </p>
            <p className="text-xs mt-1">
              {allowedTypesText}
            </p>
          </div>
        </div>
        
        {/* Error Message */}
        {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
        
        {/* Loading Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded-lg">
            <svg
              className="animate-spin h-6 w-6 text-blue-600"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};

export default LogoUploader;
